import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { day } from '../api/projects'
import {
  domainsApi, expiryHint, expiryLevel, expiryPill, hostingLabel, yesNo,
  type DomainList, type DomainListParams, type DomainRow,
} from '../api/domains'
import {
  Button, Callout, DataTable, EmptyState, FormGrid, LoadingState, Page, PageHeader, Pagination, Select, StatCard, StatGrid, StatusPill, TextField,
} from '../components/ui'
import './domains.css'

/**
 * I domini dei clienti: scadenza nel registro (scaduto in rosso, entro 30 giorni in arancio, entro 90 in giallo),
 * prossimo pagamento a iWebLab, rinnovo automatico, se il sito è ospitato da noi e dove, chi gestisce i DNS.
 * La piattaforma li aggiorna da sola (`POST /api/v1/domains/sync`); qui si abbinano ai clienti e si annotano.
 */
const PAGE_SIZE = 50

type Hosted = '' | 'si' | 'no'
type Expiry = '' | 'scaduti' | '30' | '90'

export function DomainsPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const customerId = Number(params.get('customerId')) || undefined
  const [hosted, setHosted] = useState<Hosted>('')
  const [expiry, setExpiry] = useState<Expiry>('')
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<DomainList | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Solo l'ultima richiesta aggiorna la pagina: una risposta lenta di un filtro vecchio non sovrascrive quella nuova.
  const request = useRef(0)
  const load = useCallback(async () => {
    const mine = ++request.current
    setLoading(true)
    setError('')
    const filters: DomainListParams = {
      q: query, page, pageSize: PAGE_SIZE,
      hostedByUs: hosted === 'si' ? true : hosted === 'no' ? false : null,
      expiringWithinDays: expiry === '30' ? 30 : expiry === '90' ? 90 : null,
      expired: expiry === 'scaduti',
      ...(customerId ? { customerId } : {}),
    }
    try {
      const r = await domainsApi.list(filters)
      if (mine === request.current) setData(r)
    } catch (e) {
      if (mine === request.current) { setData(null); setError(e instanceof Error ? e.message : 'Errore nel caricamento dei domini.') }
    } finally {
      if (mine === request.current) setLoading(false)
    }
  }, [hosted, expiry, query, page, customerId])

  useEffect(() => { void load() }, [load])
  useEffect(() => { setPage(1) }, [customerId])
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1) }, 300)
    return () => clearTimeout(t)
  }, [search])

  const items = data?.items ?? []
  return (
    <Page>
      <PageHeader
        title="Domini"
        icon="language"
        {...(data ? { count: data.total } : {})}
        actions={
          <Button variant="filled" icon="add" onClick={() => navigate(`/domains/new${customerId ? `?customerId=${customerId}` : ''}`)}>
            Nuovo dominio
          </Button>
        }
      />
      <div className="d-flex flex-column gap-3">
        {customerId && (
          <Callout tone="info">
            Filtrati per <Link to={`/customers/${customerId}`}>cliente n. {customerId}</Link> · <Link to="/domains">mostra tutti</Link>
          </Callout>
        )}
        {data && (
          <StatGrid>
            <StatCard label="Scaduti" value={data.expiredCount.toLocaleString('it-IT')} />
            <StatCard label="In scadenza entro 30 giorni" value={data.expiringSoonCount.toLocaleString('it-IT')} />
            <StatCard label="Ospitati da noi" value={data.hostedCount.toLocaleString('it-IT')} />
          </StatGrid>
        )}
        <FormGrid columns={3}>
          <div><TextField label="Cerca" placeholder="Dominio o cliente" value={search} onChange={e => setSearch(e.target.value)} /></div>
          <div>
            <Select label="Ospitato" value={hosted} onChange={e => { setHosted(e.target.value as Hosted); setPage(1) }}>
              <option value="">Tutti</option>
              <option value="si">Ospitati da noi</option>
              <option value="no">Esterni o senza sito</option>
            </Select>
          </div>
          <div>
            <Select label="Scadenza" value={expiry} onChange={e => { setExpiry(e.target.value as Expiry); setPage(1) }}>
              <option value="">Tutte</option>
              <option value="scaduti">Scaduti</option>
              <option value="30">Entro 30 giorni (e scaduti)</option>
              <option value="90">Entro 90 giorni (e scaduti)</option>
            </Select>
          </div>
        </FormGrid>
        {error && <Callout tone="error">{error}</Callout>}
        {loading ? (
          <LoadingState />
        ) : error ? null : items.length === 0 ? (
          <EmptyState icon="language" title="Nessun dominio." />
        ) : (
          <>
            <DomainsTable items={items} today={data?.today ?? ''} showCustomer />
            <Pagination page={page} pageSize={PAGE_SIZE} total={data?.total ?? 0} onChange={setPage} />
          </>
        )}
      </div>
    </Page>
  )
}

/** La scadenza con l'evidenza: data e quanto manca, colorati per urgenza. */
export function ExpiryCell({ expiresOn, today }: { expiresOn: string | null; today: string }) {
  if (!expiresOn) return <span className="text-muted">—</span>
  const level = expiryLevel(expiresOn, today)
  const pill = expiryPill(level)
  if (level === 'ok') return <span className="text-nowrap">{day(expiresOn)}</span>
  return (
    <StatusPill tone={pill.tone} className={pill.className} title={expiryHint(expiresOn, today)}>
      {day(expiresOn)} · {expiryHint(expiresOn, today)}
    </StatusPill>
  )
}

/** Tabella dei domini, usata qui e nella scheda del cliente (senza colonna cliente). */
export function DomainsTable({ items, today, showCustomer = false }: { items: DomainRow[]; today: string; showCustomer?: boolean }) {
  return (
    <DataTable>
      <thead>
        <tr>
          <th>Dominio</th>
          {showCustomer && <th>Cliente</th>}
          <th>Scadenza</th>
          <th>Pagamento iWebLab</th>
          <th>Rinnovo auto</th>
          <th>Ospitato da noi</th>
          <th>DNS</th>
        </tr>
      </thead>
      <tbody>
        {items.map(d => (
          <tr key={d.id}>
            <td>
              <Link to={`/domains/${d.id}`}>{d.name}</Link>
              {d.providerStatus && <div className="md-typescale-body-small text-muted">{d.providerStatus}</div>}
            </td>
            {showCustomer && (
              <td>
                {d.customerId !== null
                  ? <Link to={`/customers/${d.customerId}`}>{d.customerName || `Cliente n. ${d.customerId}`}</Link>
                  : <span className="text-muted">Da abbinare</span>}
              </td>
            )}
            <td><ExpiryCell expiresOn={d.expiresOn} today={today} /></td>
            <td className="text-nowrap">{day(d.providerDueOn)}</td>
            <td>{yesNo(d.autoRenew)}</td>
            <td className="text-nowrap">
              <StatusPill tone={d.hostedByUs ? 'success' : 'neutral'}>{d.hostedByUs ? 'Sì' : 'No'}</StatusPill>{' '}
              {hostingLabel(d)}
            </td>
            <td>{d.dnsProvider || '—'}</td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  )
}
